import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Trophy,
  Target,
  Clock,
  ExternalLink,
  Percent
} from 'lucide-react';
import { Link } from 'react-router-dom';
import TransactionWithCommission from '../admin/TransactionWithCommission';

const RecentTransactions = ({ transactions = [] }) => {
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'win':
        return Trophy;
      case 'bet':
        return Target;
      case 'initial_deposit':
        return DollarSign;
      case 'withdrawal':
        return TrendingDown;
      default:
        return DollarSign;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'win':
        return 'success';
      case 'bet':
        return 'warning';
      case 'initial_deposit':
        return 'primary';
      case 'withdrawal':
        return 'danger';
      default:
        return 'gray';
    }
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'win':
        return 'Ganancia';
      case 'bet':
        return 'Apuesta';
      case 'initial_deposit':
        return 'Depósito';
      case 'withdrawal':
        return 'Retiro';
      default:
        return 'Transacción';
    }
  };

  const formatAmount = (amount) => {
    const isPositive = amount >= 0;
    const formattedAmount = Math.abs(amount).toLocaleString();
    return {
      display: `${isPositive ? '+' : '-'}$${formattedAmount}`,
      isPositive
    };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Ahora mismo';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)}h`;

    return date.toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Actividad Reciente
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Últimas transacciones del sistema
          </p>
        </div>

        <Link
          to="/admin/transactions"
          className="btn-secondary text-sm"
        >
          Ver todas
          <ExternalLink className="w-4 h-4 ml-2" />
        </Link>
      </div>

      {/* Transactions List */}
      {transactions.length === 0 ? (
        <div className="text-center py-8">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            No hay transacciones recientes
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {transactions.map((transaction, index) => (
            <TransactionWithCommission
              key={transaction.id}
              transaction={transaction}
              index={index}
              showUserInfo={true}
            />
          ))}
        </div>
      )}

      {/* Summary */}
      {transactions.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-dark-700">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-center">
            <div className="py-3 px-2 md:py-4 md:px-3">
              <p className="text-lg font-bold text-success-600 dark:text-success-400">
                {transactions.filter(t => t.type === 'win').length}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Ganancias
              </p>
            </div>

            <div className="py-3 px-2 md:py-4 md:px-3">
              <p className="text-lg font-bold text-warning-600 dark:text-warning-400">
                {transactions.filter(t => t.type === 'bet').length}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Apuestas
              </p>
            </div>

            <div className="py-3 px-2 md:py-4 md:px-3">
              <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                ${transactions
                  .filter(t => t.commission_amount)
                  .reduce((sum, t) => sum + parseFloat(t.commission_amount || 0), 0)
                  .toLocaleString()}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center justify-center mt-1">
                <Percent className="w-3 h-3 mr-1" />
                Comisiones
              </p>
            </div>

            <div className="py-3 px-2 md:py-4 md:px-3">
              <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                ${transactions
                  .filter(t => t.type === 'win')
                  .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
                  .toLocaleString()}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Pagado a Clientes
              </p>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RecentTransactions;